import React, { useState, useEffect } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import { DeleteOutline, AddCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { BACKEND_BASE_URL } from "../config";
import { toast } from "react-toastify";

// ✅ URLs
export const EVENT_CORRECTIONS_URL = (eventId) =>
  `${BACKEND_BASE_URL}/api/capture/events/${eventId}/corrections/`;

export const DELETE_EVENT_CORRECTION_URL = (eventId, key) =>
  `${BACKEND_BASE_URL}/api/capture/events/${eventId}/corrections/${key}/`;

const CorrectionDictonaryAudioDialog = ({ open, onClose, eventId, getToken }) => {
  const [corrections, setCorrections] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  // Fetch event corrections
  const fetchCorrections = async () => {
    setLoading(true);
    try {
      const res = await axios.get(EVENT_CORRECTIONS_URL(eventId), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setCorrections(res.data.correction_dict || {});
    } catch (err) {
      toast.error("Failed to fetch corrections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && eventId) fetchCorrections();
  }, [open, eventId]);

  // Add or update correction
  const handleSaveCorrections = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        EVENT_CORRECTIONS_URL(eventId),
        { corrections },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setCorrections(res.data.correction_dict);
      toast.success("Corrections saved successfully!");
    } catch (err) {
      toast.error("Failed to save corrections");
    } finally {
      setSaving(false);
    }
  };

  // Delete correction key
  const handleDelete = async (key) => {
    try {
      await axios.delete(DELETE_EVENT_CORRECTION_URL(eventId, key), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const updated = { ...corrections };
      delete updated[key];
      setCorrections(updated);
      toast.success(`Deleted correction: ${key}`);
    } catch (err) {
      toast.error("Failed to delete correction");
    }
  };

  // Add a new correction key/value pair locally
  const handleAddNew = () => {
    if (!newKey.trim() || !newValue.trim()) {
      toast.error("Key and value required");
      return;
    }
    setCorrections({ ...corrections, [newKey.trim()]: newValue.trim() });
    setNewKey("");
    setNewValue("");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          p: 4,
          bgcolor: "background.paper",
          borderRadius: 2,
          width: 600,
          mx: "auto",
          my: "10%",
          boxShadow: 3,
        }}
      >
        <Typography variant="h6" mb={2}>
          Correction Dictionary
        </Typography>

        {loading ? (
          <Box textAlign="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {Object.entries(corrections).map(([key, value]) => (
              <Grid container spacing={1} alignItems="center" key={key} mb={1}>
                <Grid item xs={5}>
                  <TextField
                    size="small"
                    fullWidth
                    value={key}
                    disabled
                    label="Incorrect Word"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Correct Word"
                    value={value}
                    onChange={(e) =>
                      setCorrections({ ...corrections, [key]: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton color="error" onClick={() => handleDelete(key)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Grid container spacing={1} alignItems="center" mt={2}>
              <Grid item xs={5}>
                <TextField
                  size="small"
                  fullWidth
                  label="New Incorrect Word"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="New Correct Word"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton color="primary" onClick={handleAddNew}>
                  <AddCircleOutline fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveCorrections}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Corrections"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default CorrectionDictonaryAudioDialog;
